import { Cross2Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import {
  CommonTextInput,
  DEFAULT_TEXT_DEBOUNCE_MS,
} from 'components/common/form/text';
import { MachineSettingsDateRangeInput } from 'components/sections/admin-portal/machines/dialogs/settings/common/machine-settings-date-range-input';
import { useMachineErrorsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineErrorsDialogState';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import {
  ErrorTableStaticValues,
  IErrorLevelWithNA,
} from 'interfaces/i-machine-errors-table';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { ErrorLevel } from 'lib_ts/enums/errors.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { first } from 'lodash';
import { useEffect, useState } from 'react';
import { AdminMachinesService } from 'services/admin/machines.service';

const BOOLEAN_OPTIONS: IOption[] = Object.values(ErrorTableStaticValues).map(
  (m) => ({
    label: m,
    value: m,
  })
);

const LEVEL_OPTIONS: IOption[] = Object.values(ErrorLevel).map((m) => {
  const o: IOption = {
    label: m.toUpperCase(),
    color: NotifyHelper.getColorFromLevel(m),
    // todo: do we want to disable any entries that have no corresponding data?
    // disabled: ???
    value: m,
  };

  return o;
});

export const MachineErrorsToolbar = () => {
  const machine = useMachinesPortalStore((s) => s.machine);

  const [errorTypeIDs, setErrorTypeIDs] = useState<IOption[]>([]);

  useEffect(() => {
    if (!machine?.machineID) {
      return;
    }

    AdminMachinesService.getInstance()
      .getMachineErrorTypes(machine.machineID)
      .then((result) =>
        setErrorTypeIDs(
          result
            .sort((a, b) => {
              if (a.deleted === b.deleted) {
                // they are both deleted/not, sort by errorID
                return StringHelper.safeSort(a.errorID, b.errorID);
              }

              if (a.deleted) {
                // a is deleted, b is not
                return 1;
              }

              // b is deleted, a is not
              return -1;
            })
            .map((m) => ({
              label: m.errorID,
              value: m.errorID,
              group: m.deleted ? 'Inactive' : 'Active',
            }))
        )
      );
  }, [machine?.machineID]);

  const { errorFilters, setErrorFilter } = useMachineErrorsDialogState();

  return (
    <CommonFormGrid columns={6} gap={RADIX.FLEX.GAP.SM}>
      <CommonSearchInput
        id="errors-error-type-ID"
        options={errorTypeIDs}
        values={[errorFilters.errorTypeID]}
        onChange={(v) => setErrorFilter({ errorTypeID: first(v) })}
        placeholder="Error ID"
        optional
      />
      <MachineSettingsDateRangeInput
        dateRange={errorFilters.dateRange}
        onChange={(dateRange) => setErrorFilter({ dateRange })}
        showTime
      />
      <CommonSearchInput
        id="errors-level"
        placeholder="common.level"
        options={LEVEL_OPTIONS}
        values={errorFilters.level}
        onChange={(v) =>
          setErrorFilter({
            level: v as IErrorLevelWithNA[],
          })
        }
        hierarchy="asc"
        skipSort
        multiple
      />
      <CommonSelectInput
        id="errors-internal"
        options={BOOLEAN_OPTIONS}
        value={errorFilters.internal}
        onChange={(v) =>
          setErrorFilter({ internal: v as ErrorTableStaticValues })
        }
        placeholder="Internal"
        skipSort
        optional
      />
      <CommonSelectInput
        id="errors-slack"
        options={BOOLEAN_OPTIONS}
        value={errorFilters.slack}
        onChange={(v) => setErrorFilter({ slack: v as ErrorTableStaticValues })}
        placeholder="In Slack"
        skipSort
        optional
      />
      <CommonTextInput
        id="errors-error-sender"
        name="name"
        placeholder="Sender"
        value={errorFilters.sender}
        onChange={(v) => setErrorFilter({ sender: v })}
        debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
        rightSlot={
          errorFilters.sender.length === 0 ? (
            <MagnifyingGlassIcon />
          ) : (
            <Cross2Icon
              onClick={() => setErrorFilter({ sender: '' })}
              className="cursor-pointer"
            />
          )
        }
      />
    </CommonFormGrid>
  );
};
